import {differenceInSeconds} from 'date-fns';
import {Button, Card} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  TelephonyCallDetail,
  TelephonyCallRecordingAvailability,
  useGetTelephonyCallDetailQuery,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {CallRecordingPlayer, formatCallDuration} from '@dms/shared';

import {DOT_CHARACTER, parseDate, RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

/** How often a recording still being stored is checked, and for how long. */
const PROCESSING_POLL_MS = 15_000;
const PROCESSING_WINDOW_SECONDS = 300;

interface CallRecordingCardProps extends RequiredTestIdProps {
  call: TelephonyCallDetail;
  recording: TelephonyCallRecordingAvailability;
}

/**
 * The call's recording on its detail page: a player once it is stored, otherwise why not. While it
 * is being stored the card re-reads the call for a few minutes, then leaves it to a Refresh link.
 * Under the state, one line of evidence on how the caller was told about the recording.
 */
export function CallRecordingCard(props: CallRecordingCardProps) {
  const formatDateTime = useDateTimeFormatter();
  const [isPlaying, startPlaying] = useBoolean(false);
  const [shownAt] = useState(() => new Date());

  const isProcessing = props.recording.status === 'processing';
  // Read on render: each poll re-renders the card, so the window closes at the poll after five
  // minutes, and the Refresh link shows from then on.
  const isWithinPollWindow = differenceInSeconds(new Date(), shownAt) < PROCESSING_WINDOW_SECONDS;
  const {refetch, isFetching} = useGetTelephonyCallDetailQuery(
    {callId: props.call.id},
    {pollingInterval: isProcessing && isWithinPollWindow ? PROCESSING_POLL_MS : 0}
  );

  const formatTime = (value: string) => formatDateTime('timeMedium', parseDate(value));
  const formatDate = (value: string | null) =>
    isNil(value) ? '' : formatDateTime('dateShort', parseDate(value));

  const notice = match(props.call)
    .returnType<string | null>()
    .with({noticeMode: 'automatic'}, (call) =>
      [
        isNil(call.noticeServedAt)
          ? null
          : i18n.t('page.telephony.recording.noticePlayed', {
              time: formatTime(call.noticeServedAt),
            }),
        isNil(call.noticeText) ? null : `“${call.noticeText}”`,
      ]
        .filter(isNotNil)
        .join(` ${DOT_CHARACTER} `)
    )
    .with({noticeMode: 'operator'}, (call) =>
      isNil(call.noticeServedAt)
        ? i18n.t('page.telephony.recording.operatorMode')
        : i18n.t('page.telephony.recording.operatorReminderPlayed', {
            time: formatTime(call.noticeServedAt),
          })
    )
    .otherwise(() => null);

  const availableUntil = isNil(props.recording.availableUntil)
    ? null
    : i18n.t('page.telephony.recording.availableUntil', {
        date: formatDate(props.recording.availableUntil),
      });
  // A call that was not recorded shows no notice evidence, even when a notice was served.
  const noticeLine =
    props.recording.status === 'none'
      ? ''
      : [availableUntil, notice].filter(isNotNilOrEmpty).join(` ${DOT_CHARACTER} `);

  const state = (text: string) => (
    <Text size="small" color="secondary" data-testid={suffixTestId('state', props)}>
      {text}
    </Text>
  );

  const body = match(props.recording.status)
    .with('available', () =>
      isPlaying ? (
        <CallRecordingPlayer callId={props.call.id} data-testid={suffixTestId('player', props)} />
      ) : (
        <HStack spacing={2} align="center">
          <Button
            variant="secondary"
            leftIcon="AV/play_arrow"
            title={i18n.t('page.telephony.recording.play')}
            onClick={startPlaying}
            data-testid={suffixTestId('play', props)}
          />
          <Show
            when={
              isNotNil(props.recording.durationSeconds) && props.recording.durationSeconds > 0
                ? props.recording.durationSeconds
                : null
            }
          >
            {(seconds) => (
              <Text size="small" color="secondary">
                {formatCallDuration(seconds)}
              </Text>
            )}
          </Show>
        </HStack>
      )
    )
    .with('processing', () =>
      isWithinPollWindow ? (
        state(i18n.t('page.telephony.recording.processing'))
      ) : (
        <HStack spacing={2} align="center" wrap>
          {state(i18n.t('page.telephony.recording.stillProcessing'))}
          <Button
            variant="link"
            size="small"
            leftIcon="navigation/refresh"
            title={i18n.t('general.actions.refresh')}
            isLoading={isFetching}
            onClick={() => refetch()}
            data-testid={suffixTestId('refresh', props)}
          />
        </HStack>
      )
    )
    .with('failed', () => state(i18n.t('page.telephony.recording.unavailable')))
    .with('expired', () =>
      state(
        i18n.t('page.telephony.recording.expired', {date: formatDate(props.recording.expiredAt)})
      )
    )
    .with('none', () => state(i18n.t('page.telephony.recording.notRecorded')))
    .exhaustive();

  const isGone = props.recording.status === 'expired' || props.recording.status === 'none';

  return (
    <Card
      variant={isGone ? 'inlineGrey' : 'default'}
      title={i18n.t('page.telephony.callDetail.recording')}
      data-testid={props['data-testid']}
    >
      <VStack spacing={2}>
        {body}
        <Show when={noticeLine !== ''}>
          <Text size="xSmall" color="secondary" data-testid={suffixTestId('notice', props)}>
            {noticeLine}
          </Text>
        </Show>
      </VStack>
    </Card>
  );
}
